/**
 * LE TABLEAU RICHE, EN DONNÉES (charte §3) — géométrie, densités, rôles de couleur, points de rupture,
 * et les deux fonctions pures dont le rendu ET la persistance ont besoin.
 *
 * ⚠️ PUR TS, ZÉRO DÉPENDANCE, ET AUCUNE COULEUR. Que des nombres, des durées et des NOMS DE RÔLE :
 * c'est ce qui permettra à un futur rendu React Native de lire les mêmes décisions sans qu'aucune ne
 * diverge. Une couleur figée ici casserait le mode sombre au premier écran.
 *
 * ⚠️ CRITÈRE DE TRI ENTRE CE FICHIER ET `theme-web.ts` : une règle qui a besoin d'un `:hover`, d'un
 * `:focus-visible`, d'un descendant, d'un `::before` ou d'une media-query ne PEUT PAS vivre en style en
 * ligne — elle va en CSS de charte. Tout le reste (nombres, durées, z-index, noms de rôle) vit ici.
 * Ce n'est pas une préférence : c'est ce qui avait laissé l'EDI et le Store sans aucun survol pendant
 * que la Console s'en réécrivait un, partiel (2026-08-11).
 *
 * ⚠️ CE FICHIER NE REDÉCLARE PAS CE QUI EXISTE DÉJÀ EN JETON ÉMIS (2026-08-16). Les hauteurs de
 * contrôle (`--h-*`), les tailles de texte (`--t-*`) et les rayons (`--r-*`) descendent jusqu'au CSS
 * depuis `theme-web.ts` : les recopier ici sous un autre nom (`actionHeight: 30`, `labelSize: 12.5`,
 * `radius: 13`) créait une seconde vérité pour la même décision — exactement la faute que la gamme de
 * hauteurs venait de corriger un cran plus haut. Ne subsiste ici que ce qui n'a PAS de jeton.
 */

export type TableAlign = 'start' | 'end' | 'center';
export type TableDensity = 'normal' | 'compact';

/**
 * Géométrie de la carte-tableau. Valeurs RELEVÉES (charte §3a + maquette back-office), pas choisies.
 *
 * ⚠️ EN-TÊTE : LA CHARTE TRANCHE (arbitrage utilisateur 2026-08-16). La maquette portait deux styles
 * (mono 10/.07em sur Articles, non-mono 10,5/700/.05em sur Journal et Stock) ; on suit la CHARTE, qui
 * est la source de DESSIN, même si l'autre est le relevé majoritaire du code. La divergence est close :
 * il n'y a plus qu'un style d'en-tête, et les ajustements visuels éventuels se feront sur la charte.
 */
export const TABLE = {
  /**
   * La TAILLE de l'en-tête n'est pas ici : c'est `--t-micro` (10 px), cran nommé de l'échelle. Sa
   * FAMILLE non plus — c'est `--font-mono`, jeton émis par le thème.
   * ⚠️ Un booléen `mono: true` a vécu ici sans aucun lecteur (le CSS écrivait la famille en littéral) :
   * un jeton que personne ne lit n'est pas une décision partagée, c'est une seconde vérité en attente
   * de diverger. Chaque valeur ci-dessous atteint le CSS émis — c'est vérifié par test.
   */
  header: {
    /**
     * TYPOGRAPHIE DE L'EN-TÊTE — alignée sur la maquette (arbitrage utilisateur 2026-08-20).
     *
     * ⚠️ ELLE ÉTAIT EN MONO 10 px / 600 / .07em, et c'était une décision prise en connaissance de
     * cause — le SPEC la note comme « divergence close, ajustements visuels ultérieurs sur la
     * charte ». L'ajustement est celui-ci : quatre écarts se cumulaient (chasse fixe, plus petit,
     * plus fin, plus espacé) et l'en-tête pesait nettement moins à l'œil que dans la maquette.
     *
     * ⚠️ LA TAILLE VIT ICI, PAS DANS L'ÉCHELLE : `--t-micro` (10 px) sert à 67 endroits du dépôt, et
     * le décaler pour un en-tête de tableau en aurait déplacé soixante-six autres.
     *
     * ⚠️ CE QUI EST MEILLEUR À L'ŒIL ENTRE DANS LA CHARTE, il n'y reste pas en exception locale : ces
     * valeurs valent pour LES SEPT TABLEAUX de toutes les apps — c'est tout l'objet de les y loger.
     */
    fontSize: '10.5px',
    letterSpacing: '.05em',
    transform: 'uppercase',
    padV: 10,
    padH: 16,
    weight: 700,
    arrowSize: 8,
    stickyZ: 2,
  },
  /** La taille de rangée vient de la DENSITÉ (`--dt-row-fs`), jamais d'un nombre posé ici. */
  row: { padH: 16 },
  /** Cellule d'ancrage : case à cocher, chevron de dépliage. */
  gutter: { check: 32, checkBox: 20, checkBorder: 1.5, expand: 28 },
  foot: { padV: 12, padH: 18, gap: 12 },
  /**
   * LARGEURS ADAPTATIVES (2026-08-16). Sous `table-layout:auto`, la largeur declaree n est plus une
   * piste imposee mais un SOCLE : le navigateur part de la et ELARGIT pour loger le contenu, jamais
   * l inverse. `growMin` protege la seule colonne qui n a pas de socle — celle qui absorbe le reste.
   * ⚠️ 220 et non le minmax(150px, 2.2fr) de la maquette : la colonne d identite porte un titre ET
   * une sous-ligne mono ; sous 220 px, l ellipse mange la sous-ligne entiere.
   */
  width: { growMin: 220 },
  /** Barre d outils : geometrie relevee sur la maquette (11/14, gouttiere 9) — elle ne suit pas le pied. */
  toolbar: { padV: 11, padH: 14, gap: 9 },
  /**
   * ⚠️ BORNAGE EXIGÉ PAR L'EN-TÊTE COLLANT, pas une préférence de mise en page : une enveloppe en
   * défilement automatique SANS hauteur maximale devient le bloc conteneur du collage tout en ne
   * défilant jamais — l'en-tête décroche silencieusement. Les deux vont donc ensemble, toujours.
   */
  scroll: { maxHeight: 'min(64vh, 760px)' },
  /** Ligne de total : elle ferme une arithmétique, elle ne fait pas partie du corps. */
  totalRow: { fontWeight: 800 },
} as const;

/**
 * LE DÉCALAGE QUI ÉGALISE L’AIR AUTOUR DU CHEVRON DE DÉPLIAGE — dérivé, jamais posé au jugé.
 *
 * ⚠️ LE PROBLÈME QU’IL CORRIGE. La case à cocher est CENTRÉE dans sa gouttière : elle laisse donc
 * `(check − checkBox) / 2` = 6 px derrière elle. À droite du chevron, c’est la première colonne qui
 * pose son `padH` de 16. Sans compensation, le chevron a 6 px d’un côté et 16 de l’autre — un
 * décentrage qu’on voit sans savoir le nommer.
 * ⚠️ CALCULÉ ET NON ÉCRIT : si la gouttière ou la case changent de taille, l’air reste égal tout seul.
 */
export const TABLE_EXPAND_LEAD: number =
  TABLE.row.padH - (TABLE.gutter.check - TABLE.gutter.checkBox) / 2;

/**
 * Les DEUX crans de densité, et rien d'autre. Le compact est OFFERT en réglage de compte, jamais imposé.
 *
 * ⚠️ C'est la densité qui fait FOI sur la hauteur de rangée, pas les relevés d'écran (56 px sur
 * Articles, 42 px sur Stock, 44 px sur Journal) : elle seule est une décision produit. Dans la maquette
 * `--row-h` était bien câblé sur `<html>` mais les deux tableaux principaux écrivaient leur hauteur en
 * dur — le réglage de densité était décoratif. Une vue qui prétend piloter doit piloter.
 */
export const TABLE_DENSITY: Record<TableDensity, { rowMinHeight: number; rowFontSize: number; headerPadV: number }> = {
  normal: { rowMinHeight: 44, rowFontSize: 12.5, headerPadV: 10 },
  compact: { rowMinHeight: 36, rowFontSize: 12, headerPadV: 8 },
};

/**
 * HAUTEUR DE RANGÉE PROPRE À UNE VUE — le relevé, quand il diffère du cran par défaut.
 *
 * ⚠️ POURQUOI CETTE EXCEPTION EXISTE, alors que la densité doit faire foi. Les trois tableaux de la
 * maquette ne sont PAS à la même hauteur (56 sur Articles, 42 sur Stock, 44 sur Journal), et ce n’est
 * pas un accident de dessin : la rangée Articles porte une VIGNETTE de 34 px. À 44, il reste 5 px
 * au-dessus et en dessous — l’image touche presque le filet, et la ligne paraît étouffée.
 * ⚠️ ELLE NE COURT-CIRCUITE PAS LA DENSITÉ, elle est MISE À L’ÉCHELLE par elle : passer en compact
 * réduit une rangée de 56 dans la même proportion qu’une de 44. Le réglage de compte continue donc
 * de piloter, ce que les hauteurs en dur de la maquette ne faisaient pas.
 */
export function rowHeightFor(density: TableDensity, releve?: number): number {
  const base = TABLE_DENSITY[density].rowMinHeight;
  if (!releve || releve <= 0) return base;
  return Math.round((releve * base) / TABLE_DENSITY.normal.rowMinHeight);
}

/** Le relevé de la rangée Articles — la seule vue dont la ligne porte une vignette. */
export const ARTICLES_ROW_HEIGHT = 56;

/**
 * Bouton « Colonnes » + son popover (charte §3b).
 *
 * ⚠️ UNE SEULE LARGEUR (274). La charte en proposait deux (238 / 274) sans dire quand employer
 * laquelle, et 238 n'existe nulle part dans le code réel : une variante qu'on ne sait pas choisir est
 * une variante qui divergera.
 * ⚠️ `offsetY: 6` MESURÉ DEPUIS LE DÉCLENCHEUR (`rect.bottom + 6`, mécanique de la maquette), et non
 * le `offsetY: 40` de la charte qui semble mesuré depuis la barre d'outils. Repris tel quel, le
 * popover se poserait 34 px trop bas.
 * ⚠️ `minHeight` est un SOUHAIT, pas un plancher : c'est la hauteur en dessous de laquelle il vaut
 * mieux basculer le panneau AU-DESSUS du déclencheur que de le laisser déborder de la fenêtre. Employé
 * comme plancher (`Math.max`), il produisait exactement le panneau coupé qu'il devait empêcher.
 */
export const TABLE_COLUMNS = {
  panel: { width: 274, offsetY: 6, edgeGap: 8, minHeight: 200, maxHeight: 540, viewportPad: 14, zScrim: 590, zPanel: 600, enterMs: 150 },
  head: { padT: 11, padH: 14, padB: 5, letterSpacing: '.07em' },
  item: { padV: 6, padH: 8, gap: 9, checkBox: 17, checkBorder: 1.5, labelWeight: 600, lockSize: 11 },
  foot: { padV: 9, padH: 14 },
} as const;

/** Barre d'actions de masse : bandeau à contraste INVERSÉ, posé AU-DESSUS de la carte. */
export const TABLE_BULK = { padV: 12, padH: 16, gap: 12, marginBottom: 14, actionPadH: 11 } as const;

/**
 * Repli en cartes et cibles tactiles.
 *
 * ⚠️ DEUX SEUILS DISTINCTS, et ce n'est pas un doublon : le tactile vise les SURFACES QU'ON TOUCHE
 * (1100), pas le repli du tableau (768). ⚠️ Et il ne se confond pas avec le seuil du RAIL (900,
 * `RAIL_BREAKPOINT`) : ces lignes ont annoncé « le rail devient un tiroir (1100) » jusqu'au
 * 2026-09-02, chiffre périmé par la passe responsive du 2026-08-24 et recopié à deux endroits. La bande 768-1100 contient l'iPad en paysage
 * (1024) et les tablettes en portrait (810) — des écrans qui gardent un vrai tableau mais qu'on touche.
 */
export const TABLE_BREAKPOINTS = { card: 768, touch: 1100, touchTarget: 44 } as const;

/**
 * Anatomie de la carte (sous `card`).
 *
 * ⚠️ AUCUN PLAFOND DE CHAMPS ici (2026-08-16). Le dessin veut « cinq champs conservés », mais un
 * plafond sans affordance « voir tout » ferait afficher à la carte MOINS que le tableau sans le dire —
 * la donnée manquante deviendrait invisible et inatteignable. Le jeton a donc été retiré plutôt que
 * laissé en promesse : à rouvrir AVEC son affordance, pas avant.
 */
export const TABLE_CARD = { border: 1, marginBottom: 10, padV: 12, padH: 14, labelWeight: 500, cellPadV: 5 } as const;

/**
 * Tailles de page offertes.
 *
 * ⚠️ Défaut à 25 LIGNES par page (arbitrage utilisateur 2026-08-16, confirmant CADRAGE §43.2). La
 * règle de CHOIX est énoncée, elle : 25 en exploitation courante · 50+ quand la ligne est courte
 * (mouvements) · 10-15 quand elle est haute (journal). Chaque tableau surcharge en connaissance de
 * cause ; le défaut n'existe que pour qu'un cinquième nombre n'apparaisse pas faute de repère (le
 * dépôt en portait déjà quatre contradictoires : 10, 14, 50, 60).
 */
export const TABLE_PAGE_SIZES = [10, 25, 50, 100] as const;
export const TABLE_DEFAULTS = { pageSize: 25, density: 'normal' as TableDensity } as const;

/**
 * RÔLES DE COULEUR — le bloc que le §3 de la charte ne porte pas (seul des dix patrons sans
 * `*_COLOR_ROLES`). Chaque valeur est un NOM DE SLOT, résolu `var(--<slot>)` par le rendu web.
 *
 * ⚠️ CHAQUE RÔLE A UN CONSOMMATEUR, et c'est ce qui fait la différence entre une table de rôles et une
 * décoration : le CSS de `theme-web.ts` interpole `tableColorVar()` au lieu d'écrire `var(--ink-50)` à
 * la main. Un rôle sans site d'emploi a été RETIRÉ (2026-08-16) — il ne documentait rien, il offrait
 * seulement deux vérités libres de diverger.
 * ⚠️ DEUX CANAUX QUI NE SE CONFONDENT JAMAIS : le SURVOL est évaporable (`ink-50`), la SÉLECTION est
 * persistante (`ok-bg`/`ok-fg` + arête `teal-500`) — exactement la règle déjà posée par `.row-pick` et
 * `.chip.active`. Les confondre rend impossible de savoir ce qui est choisi quand la souris passe, et
 * c'est précisément la sélection qui pilote des actions de masse.
 * ⚠️ AUCUN NOM ÉCRIT DE MÉMOIRE : un `var(--…)` inexistant ne provoque aucune erreur et casse en
 * silence (mesuré à 1,47:1 de contraste en sombre parce que `--teal-50` n'existait pas). Chaque slot
 * ci-dessous est émis par `webThemeVars()` — c'est vérifié par test, pas par relecture.
 */
export const TABLE_COLOR_ROLES = {
  headerBg: 'ink-50', headerInk: 'ink-500', headerRule: 'ink-200', headerActiveInk: 'teal-700', headerArrow: 'ink-300',
  rowDivider: 'ink-100', rowHover: 'ink-50', rowInk: 'ink-800',
  /** Ligne modifiée non enregistrée (§19.1). */
  rowPendingBg: 'amber-bg', rowPendingEdge: 'amber',
  /** Ligne inactive / archivée. */
  rowMutedInk: 'ink-400',
  totalRowBg: 'teal-100', totalRowInk: 'teal-800',
  bulkBarBg: 'ink-900', bulkBarInk: 'paper',
  footBg: 'ink-50', footRule: 'ink-200',
  /** Colonne d'identité dans le popover : cadenas et libellé éteints. */
  lockedInk: 'ink-400',
  /** Carte qui enveloppe le tableau. */
  cardRule: 'ink-200', cardBg: 'paper',
  /**
   * Les états SANS lignes (chargement, attente, échec).
   * ⚠️ Ils sont entrés dans la table de rôles le 2026-08-16 parce que le rendu les écrivait en style
   * EN LIGNE (`var(--ink-500)`, `var(--danger-ink)`…) alors que son propre en-tête promet « aucune
   * couleur écrite ici ». Une promesse tenue par la vigilance ne tient pas : on déplace la décision.
   */
  stateInk: 'ink-500', stateIdleInk: 'ink-400',
  errorInk: 'danger-ink', errorDetailInk: 'ink-600', errorBg: 'danger-bg',
} as const;

export type TableColorRole = keyof typeof TABLE_COLOR_ROLES;

/** Résout un rôle en référence CSS. Passe par ici plutôt que d'écrire `var(--…)` à la main. */
export function tableColorVar(role: TableColorRole): string {
  return `var(--${TABLE_COLOR_ROLES[role]})`;
}

/**
 * LE CADENAS EST SCINDÉ EN DEUX PROPRIÉTÉS (arbitrage utilisateur 2026-08-16), et les trois recettes
 * qui les lisent vivent ICI, en un seul exemplaire.
 *
 * ⚠️ POURQUOI DEUX PROPRIÉTÉS : un seul cadenas recouvrait deux intentions — la charte disait
 * « verrouillée = ni masquable NI déplaçable », le CADRAGE disait que Famille/Prix TTC/Stock sont
 * verrouillées en visibilité mais SE DÉPLACENT. `locked` = non masquable · `pinned` = place imposée.
 * Une colonne peut donc être verrouillée en visibilité TOUT EN restant déplaçable.
 * ⚠️ POURQUOI ICI ET PAS DANS LE RENDU : le tableau et son popover Colonnes prennent la MÊME décision
 * (quel ordre, quelle bascule, quel déplacement). Recopiée des deux côtés, elle a déjà divergé une
 * fois — le rendu classait les épinglées avec sa propre formule pendant que le panneau appelait
 * `pinnedRank`, et deux commentaires affirmaient chacun être la source unique.
 */
export interface ColumnLockState {
  /** Colonne qui IDENTIFIE la ligne : non masquable. */
  locked?: boolean;
  /** Défaut : `!locked`. */
  hideable?: boolean;
}
export interface ColumnPinState {
  /** Épinglage : le rendu la ramène toujours à son bord, quel que soit l'ordre mémorisé. */
  pinned?: 'start' | 'end';
}

/** Rang d'épinglage d'une colonne : 0 = bord gauche, 1 = flux, 2 = bord droit. */
export function pinnedRank(pinned?: 'start' | 'end'): number {
  return pinned === 'start' ? 0 : pinned === 'end' ? 2 : 1;
}

/**
 * Range des colonnes par bord, en conservant leur ordre relatif d'origine.
 *
 * ⚠️ C'est CETTE fonction — pas seulement `pinnedRank` — qui doit être partagée : le tableau ramène
 * les épinglées à leur bord, et un panneau qui les listerait dans l'ordre mémorisé annoncerait un
 * ordre que l'écran ne montre pas. Le tri est STABLE (index d'origine en départage) : deux colonnes
 * d'un même bord ne peuvent pas permuter d'un rendu à l'autre sans qu'aucun réglage n'ait changé.
 */
export function sortByPinnedRank<T>(items: readonly T[], pinnedOf: (item: T) => 'start' | 'end' | undefined): T[] {
  return items
    .map((item, i) => ({ item, i }))
    .sort((a, b) => pinnedRank(pinnedOf(a.item)) - pinnedRank(pinnedOf(b.item)) || a.i - b.i)
    .map((x) => x.item);
}

/** Une colonne est masquable si elle le déclare, sinon si elle n'identifie pas la ligne. */
export function isColumnHideable(col: ColumnLockState): boolean {
  return col.hideable ?? !col.locked;
}

/**
 * Une colonne épinglée n'est PAS déplaçable : sa place est imposée par le rendu, pas par le réglage.
 * ⚠️ Le popover ne lui offre donc plus Monter/Descendre du tout (arbitrage 2) : les offrir éteints
 * laissait un bouton gris dont la raison n'atteignait jamais l'utilisateur — un contrôle `disabled`
 * ne reçoit aucun événement de survol, donc son infobulle ne s'affiche pas.
 */
export function isColumnMovable(col: ColumnPinState): boolean {
  return !col.pinned;
}

/** Réglage mémorisable d'un tableau, PAR UTILISATEUR × PAR TABLE. */
export interface ColumnLayout {
  /** Ordre complet des clés déplaçables. */
  order: string[];
  /** Clés masquées. */
  hidden: string[];
}

/** Version de la clé de persistance. Incrémenter invalide les réglages mémorisés. */
export const TABLE_PREFS_VERSION = 1;

/**
 * Clé canonique du réglage : `table-prefs:v1:<tableId>`.
 *
 * ⚠️ Fournie ICI alors que le composant n'écrit NULLE PART, et c'est volontaire : la persistance exigée
 * est « par utilisateur × par table, EN BASE ». Le composant ne peut connaître ni le client HTTP ni la
 * session ; il fournit la mécanique, l'app fournit le stockage.
 */
export function tablePrefsKey(tableId: string, version: number = TABLE_PREFS_VERSION): string {
  return `table-prefs:v${version}:${tableId}`;
}

/**
 * Clé canonique des FILTRES mémorisés : `table-filters:v1:<tableId>`.
 *
 * ⚠️ Les filtres se persistent PAR UTILISATEUR × PAR ÉCRAN (arbitrage utilisateur 2026-08-16) — même
 * portée que les colonnes, donc même mécanique et une clé qui ne peut pas se collisionner avec elles.
 * ⚠️ ET LA CONDITION QUI VA AVEC, non négociable : la restauration ne doit JAMAIS être silencieuse.
 * Le composant rend ses chips dès qu'on les lui passe et ne les conditionne à rien d'autre ; rouvrir
 * un écran fiscal sur un filtre invisible ferait croire qu'on voit tout. Comme pour les colonnes, le
 * composant fournit la mécanique, l'app fournit le stockage.
 */
export function tableFiltersKey(tableId: string, version: number = TABLE_PREFS_VERSION): string {
  return `table-filters:v${version}:${tableId}`;
}

/**
 * Réconcilie un ordre mémorisé avec les colonnes RÉELLEMENT déclarées : les clés inconnues sont
 * écartées, les clés manquantes ajoutées à côté de leur voisine.
 *
 * ⚠️ C'EST LA STRATÉGIE DE MIGRATION, et elle s'applique à CHAQUE rendu — pas au seul chargement du
 * réglage. Sans elle, un réglage enregistré l'an dernier ferait DISPARAÎTRE en silence une colonne
 * ajoutée depuis : l'écran montrerait moins que ce qu'il possède sans jamais le dire (§23.0).
 * ⚠️ Pure, donc consommée à la fois par le rendu et par la couche de persistance de l'app — une seule
 * recette, pas deux qui dériveront.
 */
export function reconcileColumnLayout(
  declaredKeys: readonly string[],
  stored: Partial<ColumnLayout> | null | undefined,
): ColumnLayout {
  const declared = new Set(declaredKeys);
  const seen = new Set<string>();
  const order: string[] = [];
  for (const key of stored?.order ?? []) {
    if (!declared.has(key) || seen.has(key)) continue; // colonne disparue du modèle, ou doublon de réglage
    seen.add(key);
    order.push(key);
  }
  // ⚠️ UNE COLONNE APPARUE DEPUIS ENTRE JUSTE AVANT CELLE QUI LA SUIT DANS L'ORDRE DÉCLARÉ, VISIBLE — en queue seulement
  //    quand rien ne la suit. En queue d'office, « Prix de vente HT » arrivait après « Stock » chez tout compte qui avait
  //    un jour réglé ses colonnes, loin du prix TTC qu'elle explique.
  declaredKeys.forEach((key, i) => {
    if (seen.has(key)) return;
    seen.add(key);
    const next = declaredKeys.slice(i + 1).find((k) => order.includes(k));
    if (next === undefined) order.push(key);
    else order.splice(order.indexOf(next), 0, key);
  });
  const hidden = (stored?.hidden ?? []).filter((key, i, all) => declared.has(key) && all.indexOf(key) === i);
  return { order, hidden };
}

/**
 * Plancher de défilement horizontal, CALCULÉ depuis les largeurs déclarées des colonnes VISIBLES.
 *
 * ⚠️ JAMAIS UN NOMBRE EN DUR : la maquette porte `min-width:1262px` alors que la somme réelle de ses
 * pistes vaut ~1386 px. Ce nombre ne correspond ni aux pistes ni à une configuration nommée — recopié,
 * il ROGNE au lieu de protéger (mesuré : « Top articles » rogné de 136 px, colonne CA comprise).
 * ⚠️ Une colonne SANS largeur déclarée ne revendique aucun plancher (elle se comprime) : lui inventer
 * une largeur par défaut reproduirait exactement le nombre magique qu'on supprime ici.
 * ⚠️ AUCUN REMBOURRAGE AJOUTÉ (correctif 2026-08-16) : la même largeur sert de `<col width>` sous
 * `table-layout:fixed`, et la charte met les cellules en `box-sizing:border-box` — le rembourrage est
 * donc DÉJÀ dans la piste. L'ajouter ici le comptait deux fois : sur huit colonnes, 256 px de plancher
 * fictif qui déclenchaient un défilement horizontal là où l'écran suffisait.
 *
 * @param widths  largeurs de piste des colonnes visibles (`undefined` = non déclarée)
 * @param gutters largeur cumulée des cellules d'ancrage (case, chevron)
 */
/**
 * ⚠️ UNE COLONNE SANS LARGEUR NE VAUT PLUS ZÉRO (2026-08-16). Elle comptait zéro dans le plancher de
 * défilement, donc sous `table-layout:fixed` elle recevait réellement 0 px dès que la fenêtre passait
 * sous la somme des autres : son texte s enroulait caractère par caractère et la rangée explosait.
 * « Se comprimer » et « valoir zéro » ne sont pas la même chose — la maquette protège sa colonne
 * principale par un PLANCHER (minmax(150px, 2.2fr)), pas par une largeur.
 */
export function tableMinWidth(pistes: readonly (number | undefined | { width?: number; minWidth?: number })[], gutters: number): number {
  if (pistes.length === 0) return 0; // aucune colonne visible : aucun plancher à imposer
  let total = gutters;
  for (const p of pistes) {
    if (typeof p === 'number') total += p;
    else if (p) total += p.width ?? p.minWidth ?? 0;
  }
  return Math.round(total);
}
